import { useEffect, useRef, useState } from "react";

/**
 * Smoothly animate an integer/float counter from previous to next value.
 * Used in the KPI bar for that "mission-control" feel.
 */
export function useCounter(target, { duration = 600, decimals = 0 } = {}) {
  const [val, setVal] = useState(typeof target === "number" ? target : 0);
  const fromRef = useRef(typeof target === "number" ? target : 0);
  const startRef = useRef(0);

  useEffect(() => {
    if (typeof target !== "number" || Number.isNaN(target)) return;
    fromRef.current = val;
    startRef.current = performance.now();
    let raf = 0;
    const from = fromRef.current;
    const tick = (t) => {
      const elapsed = t - startRef.current;
      const p = Math.min(1, elapsed / duration);
      // ease-out cubic
      const e = 1 - Math.pow(1 - p, 3);
      const next = from + (target - from) * e;
      setVal(next);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, duration, decimals]);

  if (typeof target !== "number" || Number.isNaN(target)) return target ?? "—";
  return decimals > 0 ? val.toFixed(decimals) : Math.round(val);
}
